<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useTranslation } from "i18next-vue";
import sStorageWrapper from "@/api/local";

import type { PaginationMeta } from "@/types/rest";
import { EntityType, RemoteKind } from "@/types/enums";
type FilterInstancesType = { [key: string]: { [key: string]: any; selected: boolean } };

import type { TabItem } from "@/types/ui";
import type { IntegrationDriver } from "@/types/integrationInstance";
import type { TabType } from "@/types/app";

type EntitiesFilter = {
  activeTab?: TabType
  searchText?: string
  filterEntityTypes: object
  filterInstances?: object
  pagination?: object
};

import { appStateStore } from "@/stores/appState";

import { useWindowDimension } from "@/mixins/windowDimension";
import { useTiming } from "@/mixins/timing";
import { getPreviousRoute } from "@/mixins/router";
import {
  getPaginationLimit,  
  hasPaginationLimit,
  savePaginationLimit
} from "@/mixins/listing";

import TabMenu from "@/components/ui/TabMenu.vue";
import UCSearch from "@/components/ui/UCSearch.vue";
import FilterTabs from "@/components/ui/FilterTabs.vue";
import FilterDropdown from "@/components/elements/FilterDropdown.vue";
import BaseEntityList from "@/components/elements/entity/BaseEntityList.vue";
import AddDevice from "@/components/elements/entity/AddDevice.vue";
import AddRemoteIr from "@/components/remote-controller/AddRemoteIr.vue";
import AddRemoteBt from "@/components/remote-controller/AddRemoteBt.vue";
import AddDock from "@/components/dock/AddDock.vue";
import AddIntegration from "@/components/integration/AddIntegration.vue";

const { t } = useTranslation();
const route = useRoute();
const router = useRouter();
const { isSmallScreen } = useWindowDimension();
const { sleep } = useTiming();

const appState = appStateStore();

const defaultTabItem = {
  label: t('ui.all'),
  value: 'all',
};

const activeTab = ref<TabItem>(defaultTabItem);

const searchText = ref("");
const modeAdvanced = ref(false);

const elEntityList = ref();
const elAddDevice = ref();
const elAddRemoteIr = ref();
const elAddRemoteBt = ref();
const elAddDock = ref();
const elAddIntegration = ref();

const listPagination = ref();
const init = ref(true);

const filterInstances = ref<FilterInstancesType>({});
const filterEntityTypes = ref(getCustomizedEntityTypes(Object.keys(EntityType)).reduce(
  (obj: { [key: string]: any }, key: string) => {
    obj[key as keyof typeof EntityType] = {selected: false};
    return obj;
  },
  {}
));

const tabItems = [
  {
    label: t('ui.all'),
    value: 'all',
  },
  {
    label: "Bluetooth",
    value:  RemoteKind.BT,
  },
  {
    label: t('remote.infrared'),
    value: RemoteKind.IR,
  },
  {
    label: t('remote.external_remotes'),
    value: RemoteKind.EXTERNAL,
  },
]

const paramEntityTypes = computed(() => {
  if (activeTab.value.value == RemoteKind.BT || activeTab.value.value == RemoteKind.IR) {
    return 'remote';
  }

  const filter = Object.entries(filterEntityTypes.value)
    .filter(([key, value]) => value.selected === true)
    .map(([key, value]) => key)
    .join(',');

    if (filter.length < 1) {
      return getCustomizedEntityTypes(Object.keys(EntityType)).join(',');
    }

    return filter;
});

const paramInstances = computed(() => {
  return Object.entries(filterInstances.value)
    .filter(([key, value]) => value.selected === true)
    .map(([key, value]) => key)
    .join(',');
});

watch(activeTab, () => {
  if (init.value) {
    return;
  }

  if (elEntityList.value) {
    elEntityList.value.clearAssignedEntities();
  }
})

function disableEntityType(keyEntityType: string, collection: { [key: string]: any }) {
  if (collection) {
    collection[keyEntityType].selected = false;
  }
}

function getCustomizedEntityTypes(types: string[]) {
  const typesToRemove = ['activity', 'macro'];
  return types.filter(str => !typesToRemove.includes(str));
}

function goTo(item: TabItem) {
  activeTab.value = item;

  router.push({
  path: route.path,
    query: {
      ...route.query,
      category: item.value?.toLowerCase() || 'all'
    }
  });
}

function startAdd(type: string) {
  if (type == RemoteKind.IR) {
    elAddRemoteIr.value.open()
  }

  if (type == RemoteKind.BT) {
    elAddRemoteBt.value.open()
  }

  if (type == "dock") {
    elAddDock.value.open()
  }
}

function startDirectAdd() {
  if (activeTab.value.value == RemoteKind.BT) {
    elAddRemoteBt.value.open()
  } else if (activeTab.value.value == RemoteKind.IR) {
    elAddRemoteIr.value.open()
  } else {
    elAddDevice.value.open()
  }
}

function startDriverSetup(driver: IntegrationDriver) {
  elAddIntegration.value.startSetup(driver);
}

function startDriverRegister(driver: IntegrationDriver) {
  elAddIntegration.value.doStartRegisterExternal(driver);
}

function saveToStorage(pagination: PaginationMeta) {
  listPagination.value = pagination;
  savePaginationLimit(pagination.limit)
  
  const message = {
    activeTab: activeTab.value,
    searchText: searchText.value,
    filterEntityTypes: filterEntityTypes.value,
    filterInstances: filterInstances.value,
    pagination: listPagination.value,
  } satisfies EntitiesFilter;

  appState.writeMemory('entities', message);
}

onMounted(async () => {
  init.value = true;
  const currentPagePath = router.options?.history?.state?.current ? String(router.options.history.state.current) : '';
  const prevPagePath = getPreviousRoute();
  const wasChildPage = prevPagePath.includes('/entity') || prevPagePath.includes('/remote');

   if (wasChildPage) {
    const mData = appState.readMemory('entities');

    if (mData != null) {
      goTo(mData.activeTab || defaultTabItem);
      await sleep(10);
      searchText.value = mData.searchText || '';
      filterEntityTypes.value = mData.filterEntityTypes;
      filterInstances.value = mData.filterInstances as FilterInstancesType;
      listPagination.value = mData.pagination;

      if (hasPaginationLimit()) {
        const storagePagLimit = getPaginationLimit();

        if (storagePagLimit != listPagination.value.limit) {
          listPagination.value.limit = storagePagLimit;
          listPagination.value.page = 1;
        }
      }
    }
  } else if (currentPagePath.includes('category=all')) {
    goTo(tabItems[0]);
  } else if (currentPagePath.includes('category=bt')) {
    goTo(tabItems[1]);
  } else if (currentPagePath.includes('category=ir')) {
    goTo(tabItems[2]);
  } else if (currentPagePath.includes('category=external')) {
    goTo(tabItems[3]);
  }

  init.value = false;
});

</script>
<template>
  <div class="page-devices">
    <div class="page-devices__tools">
      <TabMenu
        @item-click="goTo"
        :list-data="tabItems"
        :active-tab="activeTab"
        :compact="true"
      />
      <div class="page-devices__tools__filter">
        <div class="page-devices__tools__filter__search">
          <UCSearch
            v-if="activeTab.value === 'all'"
            v-model="searchText"
            :debouncing="true"
            :small="true"
            :has-sibling="activeTab.value === 'all'"
          /> 
          <FilterDropdown
            v-if="activeTab.value === 'all'"
            :filterEntityTypes="filterEntityTypes"
            :filterInstances="filterInstances"
          />
        </div>
        <div
          v-if="activeTab.value == 'all' && (paramEntityTypes.length > 0 || paramInstances.length > 0)"
          class="page-devices__tools__filter__tabs"
        >
          <FilterTabs
            :list="filterEntityTypes"
            @remove-element="(el) => disableEntityType(el, filterEntityTypes)"
          />
          <FilterTabs
            :list="filterInstances"
            @remove-element="(el) => disableEntityType(el, filterInstances)"
          />
        </div>
      </div>
      <div class="page-devices__tools__filter__options">
        <Transition name="opacity-fast">
          <button
            v-show="elEntityList && elEntityList.hasAssignedEntities()"
            @click="elEntityList.startDelete()"
            class="button button--secondary button--icon">
            <i class="fa-light fa-trash"></i>
          </button>
        </Transition>
        <button
          @click="startDirectAdd"
          class="button button--primary"
          :class="{'button--hybrid': !isSmallScreen, 'button--secondary button--icon': isSmallScreen}">
          <i class="fa-light fa-plus"></i>
          <span v-if="!isSmallScreen">
            <template v-if="activeTab.value == RemoteKind.BT">{{ $t('remote.add_device_bt.title_short') }}</template>
            <template v-else-if="activeTab.value == RemoteKind.IR">{{ $t('remote.add_device_ir.title_short') }}</template>
            <template v-else>{{ $t('entities.add_new') }}</template>
          </span>
        </button>
      </div>
    </div>
    <div class="page-devices__body">
      <BaseEntityList
        v-if="!init"
        ref="elEntityList"
        @loaded="saveToStorage"
        :search-text="searchText"
        :filter-entity-types="paramEntityTypes"
        :filter-instances="paramInstances"
        :filter-remote-type="activeTab.value != 'all' ? activeTab.value : ''"
        :pagination="listPagination"
      />
    </div>
  </div>
  <AddDevice
    ref="elAddDevice"
    @start-add="startAdd"
    @start-driver-setup="startDriverSetup"
    @start-driver-register="startDriverRegister"
    @change-mode-advanced="(mode: boolean) =>{modeAdvanced = mode}"
  />
  <AddRemoteIr
    ref="elAddRemoteIr"
  />
  <AddRemoteBt
    ref="elAddRemoteBt"
  />
  <AddDock
    ref="elAddDock"
  />
  <AddIntegration
    ref="elAddIntegration"
    :mode-advanced="modeAdvanced"
  />
</template>
